import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaExclamationTriangle } from "react-icons/fa";
import Header from "../Components/Header";
import ConfirmModal from "../Popups/ConfirmModal";
import ErrorModal from "../Popups/ErrorModal";
import { deleteAccount } from "../api/auth.api";
import { clearSession, getStaffId } from "../utils/storage";
import { REMARKS_MAX } from "../utils/profileValidation";

const LEAVE_REASONS = [
  "",
  "No longer need the service",
  "Found an alternative",
  "Privacy concerns",
  "Too expensive",
  "Other",
];

const DeleteAccountPage = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [staffId, setStaffId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);

  useEffect(() => {
    setStaffId(getStaffId());
  }, []);

  const handleDelete = async () => {
    if (!staffId || !checked) return;
    setConfirmOpen(false);
    setDeleting(true);
    try {
      await deleteAccount(staffId);
      clearSession();
      navigate("/login");
    } catch {
      setDeleting(false);
      setErrorOpen(true);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-col pb-16 lg:pb-0">
        <main className="flex-1 flex items-start justify-center px-4 py-10">
          <div className="w-full max-w-2xl bg-surface-lowest border border-surface-container rounded-2xl shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <FaExclamationTriangle className="text-red-500" />
              </div>
              <h1 className="text-xl font-bold text-on-primary-fixed-var">Request Account Deletion</h1>
            </div>
            <p className="text-sm text-on-surface-variant mb-6">
              We&apos;re sorry to see you go. Deleting your account is permanent and cannot be undone.
            </p>

            <div className="bg-red-50 border border-red-100 rounded-xl p-5 mb-6">
              <p className="text-sm font-semibold text-red-700 mb-3">What happens when you delete your account?</p>
              <ul className="space-y-3 text-sm text-red-800">
                <li className="flex gap-2">
                  <span className="text-red-500 shrink-0">●</span>
                  <span><strong>Loss of Medical Records:</strong> All health data, history, and plans will be erased.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-red-500 shrink-0">●</span>
                  <span><strong>Wallet Balance Forfeited:</strong> Remaining wellness funds or rewards will be lost.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-red-500 shrink-0">●</span>
                  <span><strong>No Recovery:</strong> Your account cannot be reactivated and data cannot be retrieved.</span>
                </li>
              </ul>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-on-primary-fixed-var mb-2">
                  Please tell us why you are leaving (Optional)
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full p-3 border border-surface-container rounded-lg text-sm bg-surface-lowest"
                >
                  <option value="">Select a reason…</option>
                  {LEAVE_REASONS.filter(Boolean).map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-primary-fixed-var mb-2">
                  Additional Feedback (Optional)
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value.slice(0, REMARKS_MAX))}
                  placeholder="Any further details you'd like to share…"
                  rows={4}
                  maxLength={REMARKS_MAX}
                  className="w-full p-3 border border-surface-container rounded-lg text-sm resize-y"
                />
                <p className="text-xs text-on-surface-variant text-right mt-1">{feedback.length}/{REMARKS_MAX}</p>
              </div>
            </div>

            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-1 w-4 h-4 accent-primary"
              />
              <span className="text-sm text-on-primary-fixed-var">
                I acknowledge that I have read the consequences of deleting my account and understand that this action is permanent.
              </span>
            </label>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2.5 border border-surface-container rounded-lg font-medium text-on-primary-fixed-var hover:bg-surface-low"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!checked || deleting}
                onClick={() => setConfirmOpen(true)}
                className="px-6 py-2.5 bg-red-500 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-600"
              >
                {deleting ? "Deleting…" : "Confirm Deletion"}
              </button>
            </div>
          </div>
        </main>

        <footer className="border-t border-surface-container py-4 px-6 text-xs text-on-surface-variant">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <span>© {year} Elthium Health. All rights reserved.</span>
            <nav className="flex gap-4">
              <a href="/privacyandpolicy" className="hover:text-primary">Privacy Policy</a>
              <a href="/termsandconditions" className="hover:text-primary">Terms of Service</a>
              <a href="/help" className="hover:text-primary">Support</a>
            </nav>
          </div>
        </footer>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title="Delete account permanently?"
        message="All medical records, wallet balance, and account data will be permanently erased. This cannot be undone."
        confirmLabel="Delete Account"
        cancelLabel="Cancel"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      <ErrorModal
        open={errorOpen}
        title="Deletion failed"
        message="Failed to delete account. Please try again later."
        onClose={() => setErrorOpen(false)}
      />
    </div>
  );
};

export default DeleteAccountPage;
